import { Component, OnInit, OnDestroy, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { API_BASE_URL } from '../../environments/environment';
import { Router } from '@angular/router';

@Component({
  selector: 'app-user-master',
  templateUrl: './user-master.component.html',
  styleUrls: ['./user-master.component.less']
})
export class UserMasterComponent implements OnInit, OnDestroy {

  users: any[] = [];
  newUser: any = this.emptyUser();
  loading = true
  newUserPanelOpen = false;
  private pendingPrefill: { email: string; password: string; userName: string } = null;

  /**
   * RPからのpostMessage受信ハンドラ（ngOnDestroyで解除できるようクラスフィールドとして保持）
   */
  private prefillMessageListener = (event: MessageEvent) => {
    const trustedOrigin = this.trustedRpOrigin();
    if (!trustedOrigin || event.origin !== trustedOrigin) {
      return;
    }
    if (event.data && event.data.type === 'AKBS_PREFILL_USER') {
      this.pendingPrefill = { email: event.data.email, password: event.data.password, userName: event.data.userName };
      this.ngZone.run(() => {
        this.tryApplyPrefill();
      });
    }
  };

  // エリア検索リスト
  areas = []
  salaryAreas = []

  public headers = new HttpHeaders({
    "X-LoginAccessKey": localStorage.getItem("accessKey")
  });

  constructor(private http: HttpClient, private router: Router, private ngZone: NgZone) { }

  ngOnInit() {
    this.getAreas();
    this.getSalaryAreas();
    this.getUserData();

    // AKBS連携: RP(apollo_requestpost_estimation)からのユーザー登録情報の事前入力を受け付ける
    window.addEventListener('message', this.prefillMessageListener);
    const trustedOrigin = this.trustedRpOrigin();
    if (window.opener && trustedOrigin) {
      window.opener.postMessage('AKBS_PREFILL_READY', trustedOrigin);
    }
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.prefillMessageListener);
  }

  /**
   * RPから受け取った事前入力情報を反映する。
   * ユーザー一覧の取得が完了していない場合は、取得完了後（getUserData側）に再度呼び出される。
   * 既にAKBSへ登録済みのメールアドレスの場合は警告のみ表示し、事前入力は行わない。
   */
  private tryApplyPrefill() {
    if (this.loading || !this.pendingPrefill) {
      return;
    }
    const { email, password, userName } = this.pendingPrefill;
    this.pendingPrefill = null;

    if (this.isEmailAlreadyRegistered(email)) {
      alert(`このメールアドレス（${email}）は既にAKBSに登録されています。\n新規登録ではなく「ユーザの編集」から既存ユーザーをご確認ください。`);
      return;
    }

    this.newUser.userId = email;
    this.newUser.password = password;
    // DB列がcharacter varying(50)のため、事前入力値も念のため切り詰める
    this.newUser.userName = (userName || '').substring(0, 50);
    this.newUserPanelOpen = true;
  }

  /**
   * 指定のメールアドレスが既に登録済みかどうかを判定する（RP側に合わせ大文字小文字を区別する）
   * @param email メールアドレス
   */
  private isEmailAlreadyRegistered(email: string): boolean {
    return this.users.some(u => u.userId === email);
  }

  /**
   * このAKBS環境に対応するRP(apollo_requestpost_estimation)のオリジンを返す
   * @returns 信頼できるRPのオリジン（判定できない場合は空文字）
   */
  trustedRpOrigin(): string {
    const origin = window.location.origin;
    if (origin === 'https://akbs-systems.com') {
      return 'https://requestpost-systems.com';
    } else if (origin === 'https://develop.akbs-systems.com') {
      return 'https://develop.requestpost-systems.com';
    } else if (origin === 'http://localhost:4200') {
      return 'http://localhost:8100';
    }
    return '';
  }

  /**
   * 新規追加の欄の初期値（登録のあともこれに戻す。手配ありは最初から ON）
   */
  emptyUser(): any {
    return {
      id: null,
      userId: "",
      password: "",
      userName: "",
      outsource: false,
      deleteHoliday: false,
      cannotInstead: false,
      spot: false,
      userManagement: false,
      salaryCsv: false,
      salaryApprover: false,
      operationArrangement: true,
      authority: "2",
      filterArea: [],
      filterAreaSalary: []
    };
  }

  /**
   * 認証切れ（401・403）ならログイン画面へ戻す。それ以外のエラーは呼ぶ側で知らせる
   * @param error HTTP エラー
   * @returns ログイン画面へ戻したら true
   */
  isSignOut(error): boolean {
    if (error && (error.status == 401 || error.status == 403)) {
      alert("認証に失敗しました");
      this.router.navigate(['/']);
      return true;
    }
    return false;
  }

  /**
   * ユーザー一覧データ取得
   */
  getUserData() {
    this.loading = true
    this.http.get(API_BASE_URL + "/api/v1/users", {
      headers: this.headers
    }).subscribe(res=> {
      this.users = res as any[];
      for(let i = 0; i < this.users.length; i++) {
        this.users[i].authority = String(this.users[i].authority)
      }
      this.loading = false
      this.tryApplyPrefill();
    }, error => {
      this.loading = false
      if (this.isSignOut(error)) return;
      alert("ユーザーの取得に失敗しました。");
    })
  }

  /**
   * エリアのドロップダウン生成
   */
  getAreas() {
    this.http.get(
      API_BASE_URL + "/api/v1/area?type=1", {headers: this.headers}
    ).subscribe(res => {
      this.areas = res as any[];
    }, error => {
      if (this.isSignOut(error)) return;
      alert("エリアの取得に失敗しました。");
    });
  }

  /**
   * エリアのドロップダウン生成
   */
  getSalaryAreas() {
    this.http.get(
      API_BASE_URL + "/api/v1/area?type=2", {headers: this.headers}
    ).subscribe(res => {
      this.salaryAreas = res as any[];
    }, error => {
      if (this.isSignOut(error)) return;
      alert("給与のエリアの取得に失敗しました。");
    });
  }

  /**
   * 保存ボタン
   * @param user ユーザー情報
   */
  save(user: any) {
    if (!user.userId) {
      alert('メールアドレスを入力してください。')
      return
    }
    // ほかのユーザーと同じメールアドレスにはできない（mst_user の主キー）
    if (this.users.some(u => u.id !== user.id && u.userId === user.userId)) {
      alert('このメールアドレスは既に登録されています。')
      return
    }
    this.http.post(API_BASE_URL + "/api/v1/users/" + user["id"], user, {
      headers: this.headers
    }).subscribe(res => {
      this.getUserData();
      alert("ユーザ情報を更新しました");
    }, error => {
      if (this.isSignOut(error)) return;
      alert("ユーザ情報の更新に失敗しました");
    });
  }

  compareFn(d1: number, d2: number) {
    return d1 == d2;
  }

  /**
   * 削除ボタン
   * @param user ユーザー情報
   */
  delete(user: any) {
    if (window.confirm(`ユーザー：${user.userId} を削除してよろしいですか？`)) {
      this.http.post(API_BASE_URL + "/api/v1/users/delete", user, {
        headers: this.headers
      }).subscribe(res => {
        this.getUserData();
        alert("ユーザを削除しました");
      }, error => {
        if (this.isSignOut(error)) return;
        alert("ユーザの削除に失敗しました");
      });
    }
  }

  /**
   * ユーザを新規登録します．
   * @param user 
   */
  add(user: any) {
    if (!user.userId) {
      alert('メールアドレスを入力してください。')
      return
    }
    if (!user.password) {
      alert('パスワードを入力してください。')
      return
    }
    if (this.isEmailAlreadyRegistered(user.userId)) {
      alert('このメールアドレスは既に登録されています。\n新規登録ではなく「ユーザの編集」から既存ユーザーをご確認ください。')
      return
    }
    this.http.post(API_BASE_URL + "/api/v1/users", user, {
      headers: this.headers
    }).subscribe(res => {
      alert("ユーザを新規追加しました");
      this.getUserData();
      this.newUser = this.emptyUser();
    }, error => {
      if (this.isSignOut(error)) return;
      alert("ユーザの新規追加に失敗しました");
    });
  }

  /**
   * ユーザー管理権限を取得
   * @returns ユーザー管理権限
   */
  getUserManagementAuth() {
    return localStorage.getItem("userManagement");
  }
}
